'use client'
import { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { API_URL } from '@/utils/constants'

export function bitmapBlockNumber(name: unknown): string | null {
 if (typeof name !== 'string') return null
 const match = /^(0|[1-9]\d*)\.bitmap$/i.exec(name.trim())
 return match ? match[1] : null
}

/** Global sales contain IDs without names; resolve only thumbnails near view. */
export default function BitmapImage({ id, name, className }: { id?: string; name?: string; className?: string }) {
 const ref = useRef<HTMLSpanElement>(null)
 const [visible, setVisible] = useState(false)
 const [failedUrl, setFailedUrl] = useState<string | null>(null)
 const suppliedBlock = bitmapBlockNumber(name)
 useEffect(() => {
  if (suppliedBlock !== null || visible || !ref.current) return
  const observer = new IntersectionObserver(entries => {
   if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect() }
  }, { rootMargin: '160px' })
  observer.observe(ref.current)
  return () => observer.disconnect()
 }, [suppliedBlock, visible])
 const validId = typeof id === 'string' && /^[a-f0-9]{64}i\d+$/.test(id)
 const { data } = useSWR(suppliedBlock === null && visible && validId ? `${API_URL}/inscription/${id}` : null, async url => {
  const response = await fetch(url, { signal: AbortSignal.timeout(8000) })
  if (!response.ok) throw new Error('Bitmap metadata unavailable')
  return response.json()
 }, { revalidateOnFocus: false, revalidateOnReconnect: false, dedupingInterval: 3600000, errorRetryCount: 1 })
 const block = suppliedBlock ?? bitmapBlockNumber(data?.meta?.name)
 const url = block !== null ? `https://bitmap.ordinalswallet.com/${block}` : null
 return <span ref={ref} className={`${className || ''} inline-flex items-center justify-center overflow-hidden`}>
  {url && failedUrl !== url ? <img src={url} alt={`${block}.bitmap`} loading="lazy" decoding="async" className="size-full object-contain" onError={() => setFailedUrl(url)} /> : <span role="img" aria-label={block !== null ? `${block}.bitmap thumbnail unavailable` : 'Bitmap thumbnail loading or unavailable'} className="flex size-full min-h-4 min-w-4 items-center justify-center rounded bg-orange-500/10 text-orange-500"><svg viewBox="0 0 24 24" className="size-5" aria-hidden="true"><path fill="currentColor" d="M3 3h8v8H3zm10 0h8v4h-8zm0 6h8v6h-8zM3 13h8v8H3zm10 4h8v4h-8z" /></svg></span>}
 </span>
}
